import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import api from '../services/api';
import ErrorHandler from '../utils/errorHandler';
import { STORAGE_KEYS } from '../constants';

const AuthContext = createContext();

// Session timeout configuration
const IDLE_TIMEOUT = 20 * 60 * 1000; // 20 minutes in milliseconds
const WARNING_TIME = 2 * 60 * 1000; // Show warning 2 minutes before timeout
const ACTIVITY_THROTTLE = 30 * 1000; // Only update activity max once per 30 seconds

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showWarning, setShowWarning] = useState(false);

  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Handle auto-logout due to inactivity
  const handleAutoLogout = useCallback(async () => {
    try {
      await api.post('/logout');
    } catch (error) {
      ErrorHandler.logError(error, 'AuthContext:handleAutoLogout');
    } finally {
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      delete api.defaults.headers.common.Authorization;
      setUser(null);
      setIsAuthenticated(false);
      setShowWarning(false);
      // Use window.location instead of navigate since we're outside Router
      window.location.href = '/login?session_expired=1';
    }
  }, []);

  // Reset inactivity timers
  const resetTimers = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    setShowWarning(false);

    // Only set timers if user is authenticated
    if (!isAuthenticated) return;

    // Set warning timer (18 minutes)
    warningRef.current = setTimeout(() => {
      setShowWarning(true);
    }, IDLE_TIMEOUT - WARNING_TIME);

    // Set logout timer (20 minutes)
    timeoutRef.current = setTimeout(() => {
      handleAutoLogout();
    }, IDLE_TIMEOUT);
  }, [isAuthenticated, handleAutoLogout]);

  // Handle user activity to reset timer
  const handleActivity = useCallback(() => {
    const now = Date.now();

    // Throttle activity updates
    if (now - lastActivityRef.current < ACTIVITY_THROTTLE) {
      return;
    }

    lastActivityRef.current = now;
    resetTimers();
  }, [resetTimers]);

  // Handle "Stay Logged In" from warning dialog
  const handleStayLoggedIn = useCallback(() => {
    setShowWarning(false);
    resetTimers();
  }, [resetTimers]);

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) {
      return () => {};
    }

    const events = ['mousemove', 'keypress', 'click', 'scroll', 'touchstart'];

    events.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    // Start the initial timer
    resetTimers();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [isAuthenticated, handleActivity, resetTimers]);

  const initializedRef = useRef(false);

  const fetchUser = async () => {
    try {
      const response = await api.get('/user');
      setUser(response.data.user);
      setIsAuthenticated(true);
    } catch (error) {
      ErrorHandler.logError(error, 'AuthContext:fetchUser');
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      delete api.defaults.headers.common.Authorization;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (credentials) => {
    try {
      const response = await api.post('/login', credentials);
      const { token, user: loggedInUser } = response.data;
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(loggedInUser));
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      setUser(loggedInUser);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      ErrorHandler.logError(error, 'AuthContext:login');
      return { success: false, message: error.response?.data?.message || 'Login failed' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/register', userData);
      const { token, user: registeredUser } = response.data;
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(registeredUser));
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      setUser(registeredUser);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      ErrorHandler.logError(error, 'AuthContext:register');
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed',
        errors: error.response?.data?.errors,
      };
    }
  };

  const logout = async () => {
    try {
      await api.post('/logout');
    } catch (error) {
      ErrorHandler.logError(error, 'AuthContext:logout');
    } finally {
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      delete api.defaults.headers.common.Authorization;
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/user/profile', profileData);
      const updatedUser = response.data.user;
      setUser(updatedUser);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(updatedUser));
      return { success: true };
    } catch (error) {
      ErrorHandler.logError(error, 'AuthContext:updateProfile');
      return {
        success: false,
        message: error.response?.data?.message || 'Update failed',
        errors: error.response?.data?.errors,
      };
    }
  };

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      loading,
      showWarning,
      login,
      register,
      logout,
      updateProfile,
      handleStayLoggedIn,
    }),
    [user, isAuthenticated, loading, showWarning, handleStayLoggedIn]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showWarning && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#0f1115] border border-white/10 rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-amber-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Session Expiring Soon</h3>
                <p className="text-sm text-[#d8d2c5]">You&apos;ll be logged out in 2 minutes</p>
              </div>
            </div>

            <p className="text-[#d8d2c5] mb-6">
              You&apos;ve been inactive for a while. For your security, you&apos;ll be automatically
              logged out in 2 minutes unless you choose to stay logged in.
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleStayLoggedIn}
                className="flex-1 px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors"
              >
                Stay Logged In
              </button>
              <button
                type="button"
                onClick={logout}
                className="px-4 py-3 bg-white/5 hover:bg-white/10 text-[#d8d2c5] hover:text-white rounded-xl font-medium transition-colors border border-white/10"
              >
                Logout Now
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
